import { convertMbToLabel, extractFileInfo, uploadToS3, useFormHelper } from "@plura/client/utils";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import * as Sentry from '@sentry/nextjs';
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import Icon from '../../../../atoms/Icon';
import "./file-screen.css";

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const FileScreen = ({ step, leadForm, editorMode, previewMode, onContentClick, id, hiddenFields }: Props) => {
  const { onValueChange, errorMessage, value } = useFormHelper({
    id: step.blockId,
  });
  const fieldFiles = useMemo(() => {
    return (value as string)?.split(',').filter(file => file !== '') ?? [];
  }, [value]);
  const [uploadingFiles, setUploadingFiles] = useState<{
    file: File,
    loading: boolean,
    url?: string,
    errored?: boolean,
    progress?: number
  }[]>(
    fieldFiles.map(file => ({ file: new File([], file), loading: false }))
  );
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const fileSize = !leadForm.plan ? 10 : 1024;

  useEffect(() => {
    // Update the uploadingFiles state whenever fieldFiles changes
    setUploadingFiles(fieldFiles.map(file => ({ file: new File([], file), loading: false, url: file })));
  }, [fieldFiles]);

  const handleFileUpload = async (files: File[]) => {
    const newUploadingFiles = files.map(file => ({ file, loading: true, progress: 0 }));
    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    try {
      const uploadedFiles: string[] = [...fieldFiles.map(file => file)];

      const uploadPromises = files.map(file => {
        const filePath = `uploads/${leadForm.workspaceId}/conversation/${step.formId}`;
        return uploadToS3(file, filePath, (progress) => {
          setUploadingFiles(prev => prev.map((item) =>
            item.file === file ? { ...item, progress } : item
          ));
        });
      });

      const results = await Promise.all(uploadPromises);
      uploadedFiles.push(...results);

      onValueChange(uploadedFiles.join(','));
    } catch (error) {
      toast.error('There was an issue uploading your media. Please try again.');
      Sentry.captureException(error);
      throw error;
    }
  };

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if (!editorMode || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <>
      <div className={cn('questionInner')}>
        <div>
          <h4
            onClick={() => handleContentClick('content')}
            className={
              cn(
                false && 'semiFade',
                'question',
              )}
          >
            <motion.span
              dangerouslySetInnerHTML={{
                __html: dynamicQuestionContent({
                  content: step.content,
                  score: currentResponseScore,
                  editMode: editorMode,
                  previewMode: previewMode ?? false,
                  hiddenFields,
                  journeyItems: leadForm.journeyItems ?? [],
                  userResponses
                })
              }}
            />
          </h4>
          {
            step.secondaryContent &&
            <p
              onClick={() => handleContentClick('secondaryContent')}
              className={
                cn(
                  false && 'semiFade',
                  'caption',
                )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.secondaryContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode: previewMode ?? false,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div className={'answerInputWrapper'}>
            <AnimatePresence initial={false}>
              <motion.div className={cn('fieldWrapper', 'fieldWrapper')}>
                <div className={'dropzoneWrapper'}>
                  <label htmlFor={`${step.blockId}-file-input`}>
                    <Icon
                      name="plus"
                      fill="currentColor"
                      className="!size-7 mb-1.5 shrink-0"
                    />
                    <p>Drag & drop or click to choose a file</p>
                    <span>
                      <Icon
                        name="info"
                        fill="currentColor"
                        className="!size-3.5 shrink-0"
                      />
                      Max file size: {convertMbToLabel(fileSize)}
                    </span>
                  </label>
                  <input
                    id={`${step.blockId}-file-input`}
                    type="file"
                    disabled={editorMode}
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        const maxSizeInBytes = fileSize * 1024 * 1024;
                        const oversizedFiles = Array.from(files).filter(file => file.size > maxSizeInBytes);

                        if (oversizedFiles.length > 0) {
                          toast.error(`One or more files exceed the ${convertMbToLabel(fileSize)} size limit.`);
                          return;
                        }

                        handleFileUpload(Array.from(files));
                      }
                    }}
                    multiple={true}
                    accept={['image/*', 'video/*'].join(',')}
                  />
                </div>
                <motion.div
                  className={'fileWrapper'}
                  initial={{ opacity: 0, y: 100 }}
                  animate={{
                    opacity: uploadingFiles && uploadingFiles.length ? 1 : 0,
                    transform: uploadingFiles && uploadingFiles.length ? 'translateY(0)' : 'translateY(20px)'
                  }}
                  exit={{ opacity: 0, y: 100 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  {
                    uploadingFiles.map(({ file, loading, url, progress }) => (
                      <div className={'file'} key={file.name}>
                        <div className={'fileHeader'}>
                          <Icon
                            name="document"
                            fill="var(--answer-color, var(--button-color))"
                            className="!size-6 shrink-0"
                          />
                          <div className={'fileInfo'}>
                            <span>{extractFileInfo(url ?? file.name).fileName}</span>
                            <span>{extractFileInfo(url ?? file.name).fileExtension}</span>
                          </div>
                        </div>

                        <div className={'fileActions'}>
                          {loading ? (
                            <div
                              className={'fileActionsLoader'}
                              role="status"
                              aria-label="Upload in progress"
                            >
                              <div className="fileUploadProcessing" aria-hidden>
                                {[0, 1, 2].map((i) => (
                                  <span
                                    key={i}
                                    className="fileUploadDot"
                                    style={{
                                      animationDelay: `${i * 0.16}s`,
                                    }}
                                  />
                                ))}
                              </div>
                              <div className={'progressBar'}>
                                {typeof progress === 'number' && (
                                  <div
                                    className={'progressFill'}
                                    style={{ width: `${progress}%` }}
                                  />
                                )}
                              </div>
                            </div>
                          ) : (
                            <>
                              {
                                url &&
                                <a href={url} target="_blank">
                                  <Icon
                                    name="download"
                                    fill="currentColor"
                                    className="!size-3.5 shrink-0"
                                  />
                                </a>
                              }
                              <div onClick={() => onValueChange(fieldFiles.filter(f => f !== file.name).join(','))}>
                                <Icon
                                  name="trash"
                                  fill="currentColor"
                                  className="!size-3.5 shrink-0"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  }
                </motion.div>

                {
                  errorMessage &&
                  <p className={'errorMessage'}>
                    {errorMessage}
                  </p>
                }

              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default FileScreen;